// Matching for the browse typeahead. Runs on every keystroke over the
// in-memory catalog, so it stays cheap and synchronous.
//
// Three rules earned by watching real searches fail:
//  1. Token-based, never subsequence. "Is the query a subsequence of the
//     target" is a command-palette trick that collapses on prose: "tomatoes"
//     matched "cusTOM decorATed sugar cOokiES".
//  2. Typo tolerance only on high-signal fields, and only from 5 characters up.
//     One edit on a 4-letter word lands "cake" on make/take/care/case/came.
//  3. Query words may match across DIFFERENT fields. "baked good" is one word
//     from the category and one from the maker's name ("...Goodies").
//
// People also shop by concept, not catalog vocabulary: "desserts", "pastries",
// "sweets", "vegan". SYNONYMS bridges that. It's a hand-kept list, which is
// exactly its limit — it covers the common food vocabulary, not the long tail.

function norm(s: string): string {
  // Strip combining marks so "jalapeño" matches "jalapeno".
  return s.toLowerCase().normalize("NFKD").replace(/\p{M}/gu, "");
}

const tokenCache = new Map<string, string[]>();

function tokenize(s: string): string[] {
  const hit = tokenCache.get(s);
  if (hit) return hit;
  const out = norm(s)
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
  // Bounded so a long session can't grow this without limit.
  if (tokenCache.size > 4000) tokenCache.clear();
  tokenCache.set(s, out);
  return out;
}

// Concept -> words that actually appear in listings, categories and titles.
// Keyed by the singular form; the caller singularizes first.
const SYNONYMS: Record<string, string[]> = {
  dessert: ["cake", "cookie", "brownie", "pie", "pastry", "baked", "sweet", "chocolate", "candy", "bar"],
  sweet: ["cake", "cookie", "brownie", "pie", "candy", "chocolate", "baked", "dessert"],
  treat: ["cookie", "cake", "brownie", "candy", "chocolate", "baked", "sweet"],
  pastry: ["croissant", "danish", "scone", "muffin", "baked", "bread", "roll", "bun"],
  good: ["baked", "goods", "goodies"],
  baking: ["baked", "bread", "cake", "cookie", "pastry"],
  bread: ["sourdough", "baguette", "loaf", "roll", "bun", "baked"],
  vegan: ["v"],
  vegetarian: ["v", "vegan"],
  gluten: ["gf", "glutenfree"],
  glutenfree: ["gf", "gluten"],
  gf: ["gluten", "glutenfree"],
  dairy: ["milk", "cheese", "butter", "yogurt", "cream", "eggs"],
  egg: ["eggs", "dairy"],
  veggie: ["produce", "vegetable", "garden"],
  vegetable: ["produce", "veggie", "garden"],
  fruit: ["produce", "berry", "apple", "peach", "melon"],
  meat: ["beef", "pork", "chicken", "lamb", "sausage", "steak"],
  jam: ["jelly", "preserve", "pantry"],
  jelly: ["jam", "preserve", "pantry"],
  preserve: ["jam", "jelly", "pantry"],
  sauce: ["salsa", "hot", "pantry", "condiment"],
  flower: ["bouquet", "floral", "flowers"],
  plant: ["seedling", "potted", "garden", "starter"],
  candle: ["wick", "wax"],
  honey: ["pantry", "raw"],
  snack: ["bar", "cookie", "chip", "cracker"],
  drink: ["beverage", "juice", "coffee", "tea", "cider"],
  produce: ["vegetable", "veggie", "fruit", "garden"],
};

// English plural -> singular, good enough for search: desserts -> dessert,
// pastries -> pastry, tomatoes -> tomato.
function singular(w: string): string | null {
  if (w.length > 4 && w.endsWith("ies")) return `${w.slice(0, -3)}y`;
  if (w.length > 4 && w.endsWith("es")) return w.slice(0, -2);
  if (w.length > 3 && w.endsWith("s") && !w.endsWith("ss")) return w.slice(0, -1);
  return null;
}

// A query word plus the other words that should count as it. `factor` discounts
// weaker evidence, so a direct hit always outranks a synonym hit. `exact` is
// for marker tokens like "v"/"gf": one or two letters prefix-match half the
// catalog ("v" hit Variety packs and Vanilla candles), so they only count as a
// whole standalone token.
type Variant = { text: string; factor: number; exact?: boolean };

const variantCache = new Map<string, Variant[]>();

function variantsFor(q: string): Variant[] {
  const hit = variantCache.get(q);
  if (hit) return hit;

  const out = new Map<string, number>([[q, 1]]);
  const base = singular(q);
  if (base) out.set(base, Math.max(out.get(base) ?? 0, 0.95));
  for (const [word, factor] of [...out]) {
    for (const syn of SYNONYMS[word] ?? []) {
      out.set(syn, Math.max(out.get(syn) ?? 0, factor * 0.7));
    }
  }

  const list = [...out].map(([text, factor]) => ({
    text,
    factor,
    exact: text !== q && text.length <= 2,
  }));
  if (variantCache.size > 2000) variantCache.clear();
  variantCache.set(q, list);
  return list;
}

// Levenshtein, abandoned as soon as it exceeds `max`. Only ever called on short
// words, and the early exit keeps the typo check from costing anything real.
function withinEdits(a: string, b: string, max: number): boolean {
  if (Math.abs(a.length - b.length) > max) return false;
  let prev = new Array(b.length + 1);
  let cur = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    cur[0] = i;
    let rowMin = cur[0];
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(
        prev[j] + 1,
        cur[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
      if (cur[j] < rowMin) rowMin = cur[j];
    }
    if (rowMin > max) return false;
    const swap = prev;
    prev = cur;
    cur = swap;
  }
  return prev[b.length] <= max;
}

// How well one query word matches one target word. 0 means no match.
function tokenScore(q: string, t: string, typos: boolean): number {
  if (q === t) return 100;
  if (t.startsWith(q)) return 85;
  if (q.length >= 4 && t.includes(q)) return 65;
  if (!typos) return 0;
  const maxEdits = q.length >= 9 ? 2 : q.length >= 5 ? 1 : 0;
  if (maxEdits > 0 && withinEdits(q, t, maxEdits)) return 50;
  return 0;
}

// A searchable field: its text, how much a hit there counts, and whether typo
// tolerance applies ("strict" = exact/prefix/contains only — use it for prose
// and for controlled vocabularies like categories).
export type SearchField = [text: string, weight: number, mode?: "fuzzy" | "strict"];

// Score a record. EVERY query word must match somewhere across the fields, but
// they may land in different ones. Returns null when the record doesn't match.
export function fuzzyScoreFields(query: string, fields: SearchField[]): number | null {
  const qs = tokenize(query);
  if (qs.length === 0) return 0;

  const prepared = fields
    .filter(([text]) => text)
    .map(([text, weight, mode]) => ({
      toks: tokenize(text),
      weight,
      typos: mode !== "strict",
    }));
  if (prepared.length === 0) return null;

  let total = 0;
  for (const q of qs) {
    const variants = variantsFor(q);
    let best = 0;
    for (const field of prepared) {
      for (const t of field.toks) {
        for (const v of variants) {
          const raw = v.exact ? (v.text === t ? 100 : 0) : tokenScore(v.text, t, field.typos);
          const s = raw * v.factor * field.weight;
          if (s > best) best = s;
        }
      }
    }
    if (best === 0) return null;
    total += best;
  }
  // Average so a longer query isn't inherently worth more.
  return total / qs.length;
}
